import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Col, Divider, Row, Upload, UploadFile } from 'antd';
import { Form } from 'antd';
import { FC, Fragment } from 'react';

export type UploadDocumentModalData = {
	agreementNo: string;
	assetName: string;
	uploadDocuments: {
		name: string;
		masterDocumentId: number;
		document: FileData;
		allowUpload: boolean;
		unitSequence: number;
	}[];
};

export type UploadDocumentModalParam = {
	title: string;
	customerId: string;
	document: UploadDocumentModalData;
};

export const useUploadDocumentModal = () => {
	return useAsyncModal<UploadDocumentModalParam, UploadDocumentModalData>();
};

export type UploadDocumentModalProps = ReturnType<
	typeof useUploadDocumentModal
>;

const UploadDocumentModal: FC<UploadDocumentModalProps> = ({
	modal,
	data,
	close,
}) => {
	const { form, fieldName } = useForm<{
		documents: {
			id: number;
			file: FileData;
		}[];
	}>();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="BACK"
			okText="SAVE"
			okButtonProps={{
				disabled: uploadMutation.isPending,
			}}
			onCancel={() => close()}
			onOk={form.submit}
			afterOpenChange={(isOpen) => {
				if (isOpen && data) {
					console.log('data', data);
					form.setFieldsValue({
						documents: data.document.uploadDocuments.map((doc) => {
							return {
								id: doc.masterDocumentId,
								file: doc.document,
							};
						}),
					});
				} else {
					form.resetFields();
				}
			}}
		>
			<FleetTitle style={{ marginBottom: 20 }}>{data?.title}</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={(values) => {
					close({
						agreementNo: data!.document!.agreementNo!,
						assetName: data!.document.assetName!,
						uploadDocuments: data!.document.uploadDocuments.map((doc) => {
							const updated = values.documents.find(
								(f) => f.id === doc.masterDocumentId,
							);

							return {
								...doc,
								document: updated?.file ?? doc.document,
							};
						}),
					});
				}}
			>
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="No Agreement">
							{data?.document.agreementNo ?? '-'}
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Asset">
							{data?.document.assetName ?? '-'}
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					{data?.document?.uploadDocuments?.map((doc, docIndex) => {
						return (
							<Fragment key={doc.masterDocumentId}>
								<Col span={12}>
									<Form.Item
										name={fieldName(['documents', docIndex, 'id'])}
										initialValue={doc.masterDocumentId}
										noStyle
									/>
									<Form.Item
										name={fieldName(['documents', docIndex, 'file'])}
										label={doc.name}
										rules={[rule.fileRequired()]}
										labelCol={{ span: 12 }}
										wrapperCol={{ span: 12 }}
										getValueProps={(value) => {
											const file = value as FileData;
											return {
												fileList: file?.id
													? ([
															{
																uid: file.id,
																name: file.name,
																thumbUrl: file.url,
															},
														] as UploadFile[])
													: [],
											};
										}}
										normalize={({ file }: { file: UploadFile }) => {
											return {
												id: file.uid,
												format: file.type,
												name: file.name,
												url: file.thumbUrl,
											} as FileData;
										}}
									>
										<Upload.Dragger
											listType="picture"
											maxCount={1}
											accept=".jpg,.jpeg,.png,.pdf"
											beforeUpload={() => false}
											onRemove={() => {
												form.setFieldValue(
													fieldName(['documents', docIndex, 'file']),
													null,
												);
												return false;
											}}
											onChange={async ({ file }) => {
												const fileid = Date.now().toString();
												const filepath = `customer/${data.customerId}/disbursement-verification`;

												try {
													const url = await uploadMutation.mutateAsync({
														file: file as unknown as File,
														fileid,
														filepath,
													});

													form.setFieldValue(
														fieldName(['documents', docIndex, 'file']),
														{
															format: 'jpeg',
															id: fileid,
															name: file.name,
															path: filepath,
															url,
														},
													);
												} catch {
													form.setFieldValue(
														fieldName(['documents', docIndex, 'file']),
														null,
													);
												}
											}}
										>
											<UploadOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
										</Upload.Dragger>
									</Form.Item>
								</Col>
								{docIndex % 2 ? (
									<Col span={24}>
										<Divider />
									</Col>
								) : null}
							</Fragment>
						);
					})}
				</Row>
			</Form>
		</Modal>
	);
};

export default UploadDocumentModal;
